/**
 * Finding localhost URLs in terminal output, and pointing them at the relay.
 *
 * An agent starts a dev server and the CLI prints `http://localhost:5173/`.
 * On the desktop that link is correct and we leave it alone. On the browser
 * client it is a lie: `localhost` is the phone, and the server is on a machine
 * somewhere else. This module finds those URLs so a link provider can rewrite
 * them to the relay tunnel.
 *
 * Detection only — nothing here touches the terminal byte stream. Rewriting
 * bytes in place would change their length and corrupt a TUI's cursor
 * arithmetic; xterm link providers work off the rendered buffer instead, which
 * is why they exist.
 */

/**
 * Hosts that mean "this machine". Anything else is somebody's real domain and
 * must be left alone — `localhost.evil.com` is a registrable domain that
 * resolves wherever its owner wants.
 */
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]'] as const

/**
 * The pattern, built rather than written out, because the host alternation and
 * the boundary rules are the fiddly part and repeating them invites drift.
 *
 *  - `(^|[^\w.-])` — the left boundary, as a CAPTURED GROUP rather than the
 *    lookbehind `(?<![\w.-])` it reads like. Lookbehind is a SyntaxError at
 *    RegExp *construction* time on iOS Safari before 16.4, and this regex is
 *    built at module scope: one old iPhone would throw on import and take
 *    every terminal down with it. The cost is that the match starts one
 *    character early, so `start` must add back `m[1].length` — see below.
 *    It rejects `notlocalhost:3000` and `sub.localhost:3000`, which a plain
 *    \b would happily match the tail of.
 *  - the scheme is optional: Vite and Next print bare `localhost:5173`.
 *  - the port is required. Bare `localhost` in prose is not a link, and a
 *    portless match would linkify the word every time an agent says it.
 *  - `(?![\w.-])` after the host stops `localhost.evil.com:3000` matching the
 *    `localhost` prefix. LookAHEAD is universally supported; only lookbehind
 *    is the portability problem.
 */
const HOST_ALT = LOOPBACK_HOSTS
  .map((h) => h.replace(/[.[\]]/g, (c) => '\\' + c))
  .join('|')

const LOCALHOST_URL = new RegExp(
  '(^|[^\\w.-])' +                    // 1: left boundary, consumed not asserted
  '(?:(https?):\\/\\/)?' +            // 2: optional scheme
  '(' + HOST_ALT + ')' +              // 3: loopback host
  '(?![\\w.-])' +                     //    host must not continue
  ':(\\d{1,5})(?!\\d)' +               // 4: required port, and NOTHING after it
  '(\\/[^\\s"\'<>`\\\\]*)?',          // 5: optional path/query/fragment
  // Case-insensitive: xterm's own WebLinksAddon detects links with
  // /(https?|HTTPS?):[/]{2}/, so it happily hands us `HTTP://LOCALHOST:5173/`.
  // Without the flag that arrived here unrecognised and, on a relay client,
  // skipped the tunnel and opened a dead in-app tab. The host alternation is
  // anchored by the boundary group and the lookahead, so matching either case
  // is safe; `scheme` and `host` are lower-cased below so callers still see
  // the normalised values their types promise.
  'gi',
)

export interface LocalhostMatch {
  /** Index of the first character of the match within the input. */
  start: number
  /** Index one past the last character. */
  end: number
  /** The matched text, exactly as it appeared. */
  text: string
  /** 'http' or 'https'. Defaults to http when the URL was written bare. */
  scheme: 'http' | 'https'
  host: string
  port: number
  /** Path including any query and fragment. '/' when none was written. */
  path: string
}

/**
 * Every localhost URL in a line of terminal output, in order.
 *
 * Ports outside 1-65535 are skipped rather than clamped: `localhost:99999` is
 * not a port, so it is not a link, and silently turning it into one would send
 * the user somewhere they never asked to go.
 *
 * That promise needs the `(?!\d)` in the pattern to hold. Without it
 * `\d{1,5}` was happy to take the first five digits of `:123456` and call the
 * result port 12345 — the range check then passed, and the user was sent to a
 * port they never named.
 */
export function findLocalhostUrls(text: string): LocalhostMatch[] {
  if (!text) return []
  const out: LocalhostMatch[] = []
  LOCALHOST_URL.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = LOCALHOST_URL.exec(text)) !== null) {
    const [full, lead, scheme, host, portRaw, path] = m
    const port = Number(portRaw)
    if (!Number.isInteger(port) || port < 1 || port > 65535) continue
    // A leading zero means it was never a port number — `:0080` is not 80.
    if (portRaw.length > 1 && portRaw[0] === '0') continue
    // The boundary character is consumed by group 1, so the URL itself starts
    // after it. Getting this wrong shifts every highlight one cell left.
    const start = m.index + lead.length
    const url = full.slice(lead.length)
    // Adjacent matches: `a:1 b:2` — rewind so the space that ended this match
    // can serve as the boundary for the next one.
    LOCALHOST_URL.lastIndex = start + url.length
    out.push({
      start,
      end: start + url.length,
      text: url,
      scheme: scheme?.toLowerCase() === 'https' ? 'https' : 'http',
      host: host.toLowerCase(),
      port,
      path: path || '/',
    })
  }
  return out
}

/** Where the relay exposes a desktop's port. Kept next to the parser so the
 *  worker route and the client agree in one place. */
export function tunnelPath(channelId: string, port: number, path = '/'): string {
  const suffix = path.startsWith('/') ? path : '/' + path
  return `/api/channels/${encodeURIComponent(channelId)}/localhost/${port}${suffix}`
}

/**
 * The query parameter the relay accepts a token in on the first navigation.
 *
 * Must match TUNNEL_TOKEN_PARAM in cloudflare/relay/src/localhost-tunnel.ts.
 * Not imported: the app bundle and the worker are separate builds, and this is
 * a five-character string, not a dependency worth wiring up.
 */
const TUNNEL_TOKEN_PARAM = 'cf_token'

export interface TunnelTarget {
  /** Origin of the relay, e.g. https://codeflows-relay.example.workers.dev */
  relayOrigin: string
  /** The desktop's channel. */
  channelId: string
  /**
   * The viewer's Supabase access token, when there is one.
   *
   * A browser opening this URL sends no Authorization header — not on the
   * navigation, and not on any subresource the page then requests — so the
   * only way to authorise the first hop is in the URL. The relay verifies it
   * exactly as it verifies a bearer, swaps it for an HttpOnly session cookie,
   * and 302s to the same URL without it, so it does not linger in history,
   * Referer or a proxy log. Omit it and the link still works for a caller who
   * can set headers; a browser will just get a 401.
   */
  accessToken?: string | null
}

/**
 * The URL to open for a match, or null when it should be left as-is.
 *
 * Returns null on the desktop: the link already points at the right machine,
 * and sending it through the relay would be a slower route to the same place.
 */
export function rewriteToTunnel(
  match: LocalhostMatch,
  target: TunnelTarget | null,
): string | null {
  if (!target?.relayOrigin || !target.channelId) return null
  const origin = target.relayOrigin.replace(/\/+$/, '')
  const path = tunnelPath(target.channelId, match.port, match.path)
  if (!target.accessToken) return origin + path

  // The fragment never reaches the server, so the parameter has to go in front
  // of it — appending blindly would hand the relay nothing and leave the token
  // sitting in the address bar where the redirect cannot strip it.
  const hash = path.indexOf('#')
  const base = hash === -1 ? path : path.slice(0, hash)
  const fragment = hash === -1 ? '' : path.slice(hash)
  const sep = base.includes('?') ? '&' : '?'
  const token = encodeURIComponent(target.accessToken)
  return `${origin}${base}${sep}${TUNNEL_TOKEN_PARAM}=${token}${fragment}`
}
